import React, { useState } from 'react';
import { SendIcon, SmartphoneIcon, CheckCircle2Icon, ClipboardListIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
const CREW_MEMBERS = [{
  name: 'Dan Kowalski',
  role: 'Crane Operator'
}, {
  name: 'Luis Reyes',
  role: 'Rigger'
}, {
  name: 'Jake Brennan',
  role: 'Ironworker'
}, {
  name: 'Carlos Medina',
  role: 'Laborer'
}];
export function Screen18_SendQuiz({
  data,
  onNext
}: ScreenProps) {
  const [sending, setSending] = useState(false);
  const [sentMembers, setSentMembers] = useState<Set<string>>(new Set());
  const allSent = sentMembers.size === CREW_MEMBERS.length;
  const handleSend = () => {
    setSending(true);
    CREW_MEMBERS.forEach((member, i) => {
      setTimeout(() => {
        setSentMembers(prev => new Set(prev).add(member.name));
      }, 800 + i * 700);
    });
  };
  return <div className="flex flex-col h-full" data-id="element-1328">
      <div className="mb-5" data-id="element-1329">
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1330">
          Crew Understanding Quiz
        </h2>
        <p className="text-gray-500 text-sm" data-id="element-1331">
          Send a short safety quiz to verify each crew member understands the
          PTP before work begins.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-5" data-id="element-1332">
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-id="element-1333">
          <div className="flex items-center gap-3" data-id="element-1334">
            <ClipboardListIcon className="w-5 h-5 text-yellow-500 flex-shrink-0" data-id="element-1335" />
            <div data-id="element-1336">
              <p className="text-gray-900 font-semibold text-sm" data-id="element-1337">
                PTP Safety Quiz
              </p>
              <p className="text-gray-500 text-xs" data-id="element-1338">
                5 questions · Based on today's PTP
              </p>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-3 space-y-2" data-id="element-1339">
            <div className="flex items-center justify-between gap-3" data-id="element-1340">
              <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold flex-shrink-0" data-id="element-1341">
                Task
              </span>
              <span className="text-yellow-500 text-xs font-bold text-right" data-id="element-1342">
                {data.taskTitle || 'Pre-Task Plan'}
              </span>
            </div>
            <div className="flex items-start justify-between gap-3" data-id="element-1343">
              <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold flex-shrink-0 pt-0.5" data-id="element-1344">
                Questions
              </span>
              <span className="text-gray-900 text-xs font-medium text-right leading-relaxed" data-id="element-1345">
                Hazards, controls, safety procedures, emergency response
              </span>
            </div>
          </div>
        </div>

        <section data-id="element-1346">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-1347">
            Send Quiz To
          </h3>
          <div className="space-y-2" data-id="element-1348">
            {CREW_MEMBERS.map(member => <div key={member.name} className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3" data-id="element-1349">
                <SmartphoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-1350" />
                <div className="flex-1" data-id="element-1351">
                  <span className="text-gray-900 font-medium text-sm" data-id="element-1352">
                    {member.name}
                  </span>
                  <span className="text-gray-400 text-xs ml-2" data-id="element-1353">
                    · {member.role}
                  </span>
                </div>
                <AnimatePresence mode="wait" data-id="element-1354">
                  {sentMembers.has(member.name) ? <motion.div key="sent" initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 400,
                damping: 15
              }} data-id="element-1355">
                      <CheckCircle2Icon className="w-5 h-5 text-green-500" data-id="element-1356" />
                    </motion.div> : sending ? <motion.div key="sending" animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-5 h-5 rounded-full border-2 border-gray-200 border-t-yellow-500" data-id="element-1357" /> : <motion.div key="pending" className="w-2 h-2 rounded-full bg-gray-400" data-id="element-1358" />}
                </AnimatePresence>
              </div>)}
          </div>
        </section>

        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4" data-id="element-1359">
          <p className="text-yellow-700 text-sm leading-relaxed" data-id="element-1360">
            <span className="font-bold text-gray-900" data-id="element-1361">Note:</span> After sending
            the quiz to the crew, you will also complete the quiz yourself as
            the foreman.
          </p>
        </div>

        <AnimatePresence data-id="element-1362">
          {allSent && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.3
        }} className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3" data-id="element-1363">
              <CheckCircle2Icon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1364" />
              <div data-id="element-1365">
                <p className="text-green-700 font-semibold text-sm" data-id="element-1366">
                  Quiz sent to {CREW_MEMBERS.length} crew members
                </p>
                <p className="text-green-600 text-xs mt-0.5" data-id="element-1367">
                  Workers will receive the quiz on their devices.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 space-y-3 bg-gray-50" data-id="element-1368">
        {!sending ? <PrimaryButton onClick={handleSend} icon={<SendIcon className="w-5 h-5" data-id="element-1370" />} data-id="element-1369">
            Send Quiz to Crew
          </PrimaryButton> : <PrimaryButton onClick={onNext} disabled={!allSent} icon={<ClipboardListIcon className="w-5 h-5" data-id="element-1372" />} data-id="element-1371">
            {allSent ? 'Take Quiz as Foreman' : `Sending quiz… (${sentMembers.size}/${CREW_MEMBERS.length})`}
          </PrimaryButton>}
      </div>
    </div>;
}